// Privacy policy v1.0 (effective 1 September 2026), served until v1.1 takes effect on 1 November 2026
// (src/content/legal/index.ts getGdprDoc). Delete this folder after that date.
import type { LegalDoc } from "../types";

// Español (RGPD). Traducción de la política de referencia (v1.0, 1 sept. 2026).
const gdprEs: LegalDoc = {
  title: "Política de privacidad y protección de datos (RGPD / GDPR)",
  effectiveDate: "1 de septiembre de 2026",
  version: "1.0",
  intro:
    "Esta política explica cómo Photohawk trata los datos personales conforme al Reglamento General de Protección de Datos de la UE (GDPR) y a su equivalente francés, el RGPD (Règlement Général sur la Protection des Données), así como al UK GDPR. El RGPD y el GDPR designan la misma normativa.",
  sections: [
    {
      heading: "1. Quiénes somos",
      blocks: [
        {
          type: "p",
          text: "Photohawk es una plataforma de fotografía de eventos que permite a fotógrafos y organizadores publicar, buscar y vender fotos de eventos.",
        },
        {
          type: "ul",
          items: [
            "Entidad jurídica: CLOUDCONCUR LTD (número de sociedad 13399913), sociedad registrada en Inglaterra y Gales, que opera bajo el nombre comercial Photohawk.",
            "Domicilio social: 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, Reino Unido.",
            "Delegado de Protección de Datos (DPO): Andrew Hammond — contacto support@photohawk.com.",
            "Representante en la UE (Art. 27 del RGPD): no hemos designado un representante en la UE, sobre la base de que nuestro tratamiento de datos de residentes de la UE es ocasional y no constituye nuestra actividad principal (Art. 27(2)). Revisamos periódicamente este punto y designaremos un representante si nuestro tratamiento cambia.",
            "Autoridades de control: la Information Commissioner's Office (ICO) del Reino Unido para el Reino Unido, y las autoridades de la UE, incluida la CNIL francesa, para las personas situadas en la UE.",
          ],
        },
      ],
    },
    {
      heading: "2. Nuestros dos roles — responsable del tratamiento y encargado del tratamiento",
      blocks: [
        {
          type: "p",
          text: "Photohawk se dirige a dos categorías de personas, y nuestro rol jurídico difiere para cada una:",
        },
        {
          type: "ul",
          items: [
            "Fotógrafos y organizadores (nuestros clientes). Respecto a los datos de su cuenta (nombre, correo electrónico, datos profesionales, credenciales, facturación e información de pagos), Photohawk es el responsable del tratamiento.",
            "Asistentes y compradores (sus clientes). Respecto a los datos de las personas que aparecen en las fotos y que las compran, usted (el fotógrafo/organizador) es el responsable del tratamiento y Photohawk es su encargado del tratamiento — tratamos esos datos siguiendo sus instrucciones para gestionar las galerías, la búsqueda y las ventas que usted ha configurado. Un acuerdo de tratamiento de datos (véase §11) rige esta relación.",
          ],
        },
      ],
    },
    {
      heading: "3. Qué datos personales tratamos",
      blocks: [
        { type: "p", text: "Para fotógrafos/organizadores (somos responsables del tratamiento):" },
        {
          type: "ul",
          items: [
            "Identidad y contacto: nombre, correo electrónico, teléfono, razón social, dirección.",
            "Cuenta y seguridad: credenciales de acceso (a través de AWS), sesiones.",
            "Datos financieros: datos de suscripción/facturación (a través de Stripe) y datos de pagos (a través de Trolley).",
          ],
        },
        { type: "p", text: "Para asistentes/compradores (somos encargados del tratamiento, por cuenta del organizador):" },
        {
          type: "ul",
          items: [
            "Identidad y contacto: nombre, correo electrónico, número de teléfono, dirección postal/de facturación.",
            "Fotografías de los asistentes.",
            "Datos biométricos — cuando la búsqueda por rostro/selfie está activada (véase §4).",
            "Datos de pedido y de pago (tratados a través de nuestros proveedores de pago; no conservamos los números de tarjeta completos).",
            "Estado del consentimiento de marketing, cuando el asistente lo otorga.",
          ],
        },
      ],
    },
    {
      heading: "4. Búsqueda por rostro y selfie (datos biométricos de categoría especial)",
      blocks: [
        {
          type: "p",
          text: "Cuando un organizador activa la búsqueda por selfie/rostro, Photohawk crea una representación matemática de los rostros para que un asistente pueda encontrar sus propias fotos.",
        },
        {
          type: "ul",
          items: [
            "Finalidad: únicamente para asociar a una persona con sus propias fotos del evento.",
            "Ubicación: tratados y almacenados en la Unión Europea (AWS, región de Irlanda).",
            "Conservación: los datos biométricos/de rostro se eliminan automáticamente cuando una galería caduca (en un plazo de ~60 días) y cuando se elimina una galería — lo que ocurra primero.",
            "Base jurídica (Art. 9 del RGPD): los datos biométricos constituyen una categoría especial y reciben una protección adicional: solo se usan para que las personas puedan encontrar sus propias fotos en esa galería. En su condición de responsable del tratamiento de los asistentes, el administrador de la galería decide si la búsqueda por rostro es adecuada para su evento y si está permitida en el lugar donde se celebra. Cuando la legislación local exige el consentimiento de las personas fotografiadas, el administrador debe tenerlo, por ejemplo a través de la inscripción en el evento; si no lo tiene, debe usar en su lugar la búsqueda por dorsal y por hora, que no usa datos faciales. Photohawk ofrece un paso de consentimiento integrado: se solicita al asistente que otorgue su consentimiento antes de que su selfie se utilice para la búsqueda por rostro, y puede negarse.",
            "Menores: véase §13.",
          ],
        },
      ],
    },
    {
      heading: "5. Por qué usamos los datos y nuestras bases jurídicas",
      blocks: [
        {
          type: "ul",
          items: [
            "Para prestar el servicio (alojar galerías, ejecutar la búsqueda, procesar pedidos): ejecución de un contrato.",
            "Pagos, abonos, registros fiscales y contables: ejecución de un contrato y cumplimiento de una obligación legal.",
            "Búsqueda por rostro/selfie (Art. 9): el consentimiento explícito de la persona que busca, antes de usar su selfie; para las personas fotografiadas, según lo descrito en §4.",
            "Correos electrónicos/mensajes de marketing: únicamente con el consentimiento del destinatario (opt-in), que puede retirarse en cualquier momento.",
            "Cookies de analítica y publicidad: únicamente con su consentimiento, otorgado a través de nuestro banner de cookies (véase §12).",
            "Seguridad, prevención del fraude y mejora del servicio: nuestro interés legítimo, ponderado frente a sus derechos.",
          ],
        },
      ],
    },
    {
      heading: "6. Dónde se almacenan sus datos y transferencias internacionales",
      blocks: [
        {
          type: "ul",
          items: [
            "Residencia de los datos: los datos de los asistentes y de las cuentas — incluidas las fotos y los datos biométricos de rostro — se alojan en la Unión Europea (AWS, Irlanda / eu-west-1). Nuestro servicio de identidad (AWS) también se encuentra en la UE.",
            "Transferencias internacionales: algunos de nuestros subencargados del tratamiento (véase §7) operan fuera de la UE/el Reino Unido (por ejemplo, en Estados Unidos). Cuando se transfieren datos personales fuera de la UE/el Reino Unido, nos basamos en una decisión de adecuación cuando resulta aplicable y, en su defecto, en las Cláusulas Contractuales Tipo (CCT) de la Comisión Europea junto con el Addendum de Transferencia Internacional de Datos del Reino Unido, además de garantías adicionales cuando sea necesario.",
          ],
        },
      ],
    },
    {
      heading: "7. Subencargados del tratamiento",
      blocks: [
        {
          type: "p",
          text: "Recurrimos a un número reducido de proveedores cuidadosamente seleccionados para prestar el servicio, cada uno de ellos vinculado por un acuerdo de tratamiento de datos (y por las CCT/el Addendum del Reino Unido cuando tratan datos fuera de la UE/el Reino Unido):",
        },
        {
          type: "table",
          head: ["Proveedor", "Finalidad", "Datos implicados"],
          rows: [
            ["Amazon Web Services (AWS)", "Alojamiento, almacenamiento, identidad, búsqueda por rostro, correo transaccional — región de la UE", "Todos los datos personales, fotos, datos biométricos"],
            ["Cloudflare (incl. R2)", "CDN, dominios personalizados, almacenamiento de objetos", "Fotos, datos de solicitud"],
            ["Stripe", "Pagos con tarjeta y suscripciones", "Datos de pago de compradores y clientes"],
            ["Xendit", "Pagos (Sudeste Asiático)", "Datos de pago de los compradores"],
            ["Paystack", "Pagos (África)", "Datos de pago de los compradores"],
            ["Trolley", "Pagos a los fotógrafos", "Identidad y datos bancarios del beneficiario"],
            ["Postmark", "Correo transaccional", "Nombres, direcciones de correo electrónico"],
            ["Twilio / WhatsApp", "Entrega de fotos y notificaciones", "Nombres, números de teléfono"],
            ["Google (Google Analytics)", "Analítica de uso del sitio web (sujeta a consentimiento)", "Datos de uso/dispositivo, identificadores"],
            ["Meta Platforms (Meta Pixel)", "Publicidad y medición de conversiones (sujeta a consentimiento)", "Datos de uso, identificadores"],
          ],
        },
        { type: "p", text: "Puede solicitarse una lista actualizada." },
      ],
    },
    {
      heading: "8. Cuánto tiempo conservamos los datos (conservación)",
      blocks: [
        {
          type: "ul",
          items: [
            "Datos biométricos / de rostro: eliminados en un plazo de ~60 días tras la caducidad de una galería, y al eliminarse una galería (véase §4).",
            "Fotos y vídeos: la conservación la controla el titular de la cuenta (el organizador/fotógrafo). El contenido se conserva mientras el titular de la cuenta lo conserve; los titulares de cuenta pueden eliminar fotos, vídeos, galerías y fichas de asistentes en cualquier momento, y las eliminamos cuando ellos lo hacen. Photohawk no impone una vida útil fija a este contenido.",
            "Pedidos y registros financieros: conservados durante 7 años para cumplir las obligaciones fiscales y contables.",
            "Fichas de contacto de asistentes/compradores: controladas por el organizador — conservadas mientras el organizador las conserve, y eliminadas cuando el organizador las elimina o ante una solicitud de supresión verificada.",
            "Listas de marketing: conservadas hasta que se retire el consentimiento.",
            "Los registros operativos/de seguridad se conservan durante 30 días.",
          ],
        },
      ],
    },
    {
      heading: "9. Cómo protegemos los datos",
      blocks: [
        {
          type: "ul",
          items: [
            "En tránsito: cifrados mediante HTTPS/TLS en todas partes.",
            "En reposo: las fotos y el contenido se almacenan en AWS y Cloudflare R2, que cifran los datos en reposo de forma predeterminada; las credenciales sensibles (por ejemplo, tokens de cuentas conectadas) se cifran además con AWS.",
            "Controles de acceso: acceso autenticado a través de AWS; el acceso del personal se limita a lo necesario para operar el servicio.",
          ],
        },
      ],
    },
    {
      heading: "10. Sus derechos",
      blocks: [
        {
          type: "p",
          text: "Conforme al GDPR/RGPD y al UK GDPR, usted tiene derecho a: acceder a sus datos; rectificarlos; suprimirlos («derecho al olvido»); limitar el tratamiento u oponerse a él; a la portabilidad de los datos; y a retirar su consentimiento en cualquier momento. También tiene derecho a presentar una reclamación ante una autoridad de control (la ICO en el Reino Unido, o su autoridad local, como la CNIL en Francia).",
        },
        { type: "p", text: "Cómo ejercerlos:" },
        {
          type: "ul",
          items: [
            "Fotógrafos/organizadores (somos responsables del tratamiento): contacte con nuestro DPO en support@photohawk.com. Respondemos en el plazo de un mes.",
            "Asistentes/compradores (el organizador es el responsable del tratamiento): póngase en contacto con el fotógrafo u organizador que gestionó su evento. Los fotógrafos pueden eliminar los datos personales de un asistente — incluidas sus fotos y cualquier dato de búsqueda por rostro — desde dentro de Photohawk, y Photohawk ejecutará una solicitud verificada por cuenta del organizador.",
          ],
        },
        {
          type: "p",
          text: "El cierre de una cuenta de Photohawk cancela la facturación, pero no suprime por sí solo los datos almacenados; solicítenos la supresión de los datos si es eso lo que necesita.",
        },
      ],
    },
    {
      heading: "11. Acuerdo de tratamiento de datos (DPA)",
      blocks: [
        {
          type: "p",
          text: "Para los fotógrafos/organizadores, nuestro tratamiento de los datos de sus asistentes se rige por un acuerdo de tratamiento de datos conforme al Art. 28 del RGPD, que ponemos a disposición previa solicitud en support@photohawk.com.",
        },
      ],
    },
    {
      heading: "12. Cookies y tecnologías similares",
      blocks: [
        {
          type: "ul",
          items: [
            "Las cookies estrictamente necesarias (para el inicio de sesión, la seguridad y la funcionalidad básica) están siempre activas; no requieren consentimiento.",
            "Las cookies de analítica y publicidad — Google Analytics y el Meta Pixel (véase §7) — no son esenciales y se cargan únicamente después de que usted otorgue su consentimiento a través de nuestro banner de consentimiento de cookies. Puede modificar o retirar su elección en cualquier momento a través del banner / los ajustes de cookies. Rechazarlas deja el servicio plenamente utilizable.",
            "Encontrará más detalles en nuestra Política de cookies independiente.",
          ],
        },
      ],
    },
    {
      heading: "13. Datos de menores",
      blocks: [
        {
          type: "p",
          text: "Los eventos pueden incluir a menores. Decidir si la búsqueda por rostro es adecuada para un evento corresponde al administrador de la galería (véase §4); cuando puedan estar presentes menores y la legislación local exija el consentimiento de las personas fotografiadas, el administrador debe tener el consentimiento de un padre, madre o tutor para ellos, o usar en su lugar la búsqueda por dorsal y por hora. Photohawk no crea deliberadamente datos biométricos de un menor sin un consentimiento que la ley exija, y el paso de consentimiento integrado refuerza este principio.",
        },
      ],
    },
    {
      heading: "14. Violaciones de datos",
      blocks: [
        {
          type: "p",
          text: "En caso de que se produzca una violación de datos personales, Photohawk notificará al responsable del tratamiento afectado sin dilación indebida tras tener conocimiento de ella, y le asistirá en el cumplimiento de sus propias obligaciones de notificación a las autoridades de control y a las personas afectadas (Arts. 33–34 del RGPD).",
        },
      ],
    },
    {
      heading: "15. Cambios en esta política",
      blocks: [
        {
          type: "p",
          text: "Podemos actualizar esta política; la «Fecha de entrada en vigor» que figura arriba indica la versión vigente. Los cambios sustanciales se comunicarán a los titulares de cuenta.",
        },
      ],
    },
    {
      heading: "16. Contacto",
      blocks: [
        {
          type: "p",
          text: "Delegado de Protección de Datos — Andrew Hammond. CLOUDCONCUR LTD (número de sociedad 13399913), que opera bajo el nombre Photohawk. Correo electrónico: support@photohawk.com · 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, Reino Unido.",
        },
      ],
    },
  ],
};

export default gdprEs;
